import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import useAuth from "src/hooks/useAuth";
import useWizardStep from "src/hooks/useWizardStep";
import { fullWidthDialog } from "src/constants/dialog";

import Step0Introduction from "./Step0Introduction";
import Step1TransferType from "./Step1TransferType";
import Step2AFullTransfer from "./Step2AFullTransfer";
import Step2BItemSelection from "./Step2BItemSelection";
import Step3BDestinationProject from "./Step3BDestinationProject";
import ReceivingAssistantAttentionModal from "../ReceivingAssistantAttentionModal";

const STEPS = {
  intro: { index: 0 },
  type: { index: 1 },
  destination: { index: 2 },
  items: { index: 2 },
  partialDestination: { index: 3 },
};

const FULL_TRANSFER_STEPS = 3;
const PARTIAL_TRANSFER_STEPS = 4;

export default function TransferShipmentWizard({ shipment, onTransferSuccess }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentStepName = useWizardStep(Object.keys(STEPS), {
    param: "transferStep",
    fallback: "intro",
  });
  const [transferType, setTransferType] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [selectedClient, setSelectedClient] = useState(shipment?.project?.client?.id || null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAttentionModal, setShowAttentionModal] = useState(false);
  const [targetShipment, setTargetShipment] = useState(null);

  const totalSteps = transferType === "partial" ? PARTIAL_TRANSFER_STEPS : FULL_TRANSFER_STEPS;
  const currentStepIndex = STEPS[currentStepName]?.index ?? 0;

  const navigateToStep = useCallback((stepName) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("transferStep", stepName);
    navigate({ search: searchParams.toString() });
  }, [navigate]);

  const handleClose = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete("transferShipmentId");
    searchParams.delete("transferStep");
    navigate({ search: searchParams.toString() });
  }, [navigate]);

  const handleTransferTypeSelect = useCallback((type) => {
    setTransferType(type);
    navigateToStep(type === "full" ? "destination" : "items");
  }, [navigateToStep]);

  const handleFullTransferSuccess = useCallback((transferredShipment) => {
    if (onTransferSuccess) {
      onTransferSuccess();
    }
    setTargetShipment(transferredShipment);
    setShowAttentionModal(true);
  }, [onTransferSuccess]);

  const handlePartialTransferSuccess = useCallback((newShipment) => {
    if (onTransferSuccess) {
      onTransferSuccess();
    }
    setTargetShipment(newShipment);
    setShowAttentionModal(true);
  }, [onTransferSuccess]);

  const handleOpenReceivingWizard = useCallback(() => {
    const targetShipmentId = targetShipment?.id;
    const targetShipmentCode = targetShipment?.shipmentId;
    const [, , , tab] = window.location.pathname.split("/");
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete("transferShipmentId");
    searchParams.delete("transferStep");
    searchParams.set("step", "shipment");
    searchParams.set("receiving", "true");
    searchParams.set("editShipment", "true");
    if (targetShipmentCode) searchParams.set("shipmentId", targetShipmentCode);
    if (targetShipmentId) searchParams.set("id", targetShipmentId);

    const projectId = selectedProject;
    navigate({ pathname: `/projects/${projectId}/${tab}`, search: searchParams.toString() });
  }, [navigate, targetShipment, selectedProject]);

  if (showAttentionModal) {
    return (
      <ReceivingAssistantAttentionModal
        open={showAttentionModal}
        onClose={handleClose}
        onNext={handleOpenReceivingWizard}
      />
    );
  }

  const renderStep = () => {
    switch (currentStepName) {
      case "intro":
        return (
          <Step0Introduction
            shipment={shipment}
            step={currentStepIndex}
            totalSteps={totalSteps}
            onNext={() => navigateToStep("type")}
            onClose={handleClose}
          />
        );
      case "type":
        return (
          <Step1TransferType
            shipment={shipment}
            step={currentStepIndex}
            totalSteps={totalSteps}
            onBack={() => navigateToStep("intro")}
            onSelectType={handleTransferTypeSelect}
            onClose={handleClose}
          />
        );
      case "destination":
        return (
          <Step2AFullTransfer
            shipment={shipment}
            step={currentStepIndex}
            totalSteps={totalSteps}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            onBack={() => navigateToStep("type")}
            onTransferSuccess={handleFullTransferSuccess}
            onClose={handleClose}
          />
        );
      case "items":
        return (
          <Step2BItemSelection
            shipment={shipment}
            step={currentStepIndex}
            totalSteps={totalSteps}
            selectedItemIds={selectedItemIds}
            setSelectedItemIds={setSelectedItemIds}
            onBack={() => navigateToStep("type")}
            onNext={() => navigateToStep("partialDestination")}
            onClose={handleClose}
          />
        );
      case "partialDestination":
        return (
          <Step3BDestinationProject
            shipment={shipment}
            step={currentStepIndex}
            totalSteps={totalSteps}
            selectedItemIds={selectedItemIds}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            onBack={() => navigateToStep("items")}
            onTransferSuccess={handlePartialTransferSuccess}
            onClose={handleClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={handleClose} sx={fullWidthDialog}>
      <Box display="flex" flexDirection="column" height="100%">
        <Box minHeight={400} height="100%">
          {renderStep()}
        </Box>
      </Box>
    </Dialog>
  );
}
